/**
 * Data layer for `fraud-tool-access-permission-per-role`. Thin typed fetch wrappers over /api/v1/fraud-tool-access-permission-per-role.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface FraudToolAccessPermissionPerRoleRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListFraudToolAccessPermissionPerRoleResponse {
	count: number;
	items: FraudToolAccessPermissionPerRoleRow[];
}

export interface CreateFraudToolAccessPermissionPerRoleBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/fraud-tool-access-permission-per-role";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListFraudToolAccessPermissionPerRoleResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListFraudToolAccessPermissionPerRoleResponse>(res);
}

export async function create(body: CreateFraudToolAccessPermissionPerRoleBody): Promise<FraudToolAccessPermissionPerRoleRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<FraudToolAccessPermissionPerRoleRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const fraudToolAccessPermissionPerRoleQueryKeys = {
	all: ["fraud-tool-access-permission-per-role"] as const,
	list: () => [...fraudToolAccessPermissionPerRoleQueryKeys.all] as const,
	detail: (id: string) => [...fraudToolAccessPermissionPerRoleQueryKeys.all, id] as const,
};
